import { useTLang } from 'intl/intl';
import { formatCurrency } from 'utils/CurrencyUtils';

import type { OrderDetailToPrint, UserType } from '../domain';

export function useOrderInvoicePrintingLang(props: Props) {
  const {
    orderId,
    storeName,
    contact,
    paymentMethod,
    numberOfProducts,
    totalsErrorMsg,
    cashReceivedFromUser,
    cashDeliveredToUser,
    manualCode,
    userType,
  } = props;

  const orderIdAsString = orderId.toString();
  const orderIdTitle = useTLang(
    { id: 'print.invoice.orderId' },
    {
      firstDigits: orderIdAsString.slice(0, -4),
      lastDigits: orderIdAsString.slice(-4),
    },
  );
  const productsTitleP2 = useTLang(
    { id: 'print.invoice.products.title.p2' },
    { amount: numberOfProducts },
  );
  const fullAddress = `${contact.address.base}, ${contact.address.description}`;
  const totalsError = useTLang(
    { id: 'print.invoice.totals.error.msg' },
    { msg: totalsErrorMsg },
  );
  const cashReceived = useTLang(
    { id: 'print.invoice.totals.notes.cash.receivedFromUser' },
    { cash: formatCurrency(cashReceivedFromUser) },
  );
  const cashDelivered = useTLang(
    { id: 'print.invoice.totals.notes.cash.deliveredToUser' },
    { cash: formatCurrency(cashDeliveredToUser, true) },
  );
  const handshakeManualCode = useTLang(
    { id: 'print.invoice.handshake.manualCode' },
    { manualCode },
  );

  const checklistTitleP2 = useTLang(
    { id: 'print.checklist.title.p2' },
    { amount: numberOfProducts },
  );

  return {
    invoice: {
      orderIdTitle,
      info: {
        isPickup: useTLang({ id: 'print.invoice.info.isPickup' }),
        storeName: useTLang(
          { id: 'print.invoice.info.storeName' },
          { name: storeName },
        ),
        platform: useTLang({ id: 'print.invoice.info.platform' }),
        createdAt: useTLang({ id: 'print.invoice.info.createdAt' }),
        paymentMethod: useTLang(
          { id: 'print.invoice.info.paymentMethod' },
          { paymentMethod },
        ),
        contact: {
          phone: useTLang(
            { id: 'print.invoice.info.contact.phone' },
            { phone: contact.phone },
          ),
          address: useTLang(
            { id: 'print.invoice.info.contact.address' },
            { address: fullAddress },
          ),
          zipCode: useTLang(
            { id: 'print.invoice.info.contact.zipCode' },
            { zipCode: contact.zipCode },
          ),
          neighborhood: useTLang(
            { id: 'print.invoice.info.contact.neighborhood' },
            { neighborhood: contact.neighborhood },
          ),
        },
        shippingPrime: useTLang({ id: 'print.invoice.info.shippingPrime' }),
      },
      products: {
        title: {
          p1: useTLang({ id: 'print.invoice.products.title.p1' }),
          p2: productsTitleP2,
        },
        item: {
          replace: {
            suggestion: useTLang({
              id: 'print.invoice.products.item.replacement.suggestion',
            }),
          },
          comment: useTLang({ id: 'print.invoice.products.item.comment' }),
          globalOffer: useTLang({
            id: 'print.invoice.products.item.globalOffer',
          }),
        },
      },
      whims: {
        title: useTLang({ id: 'print.invoice.whims.title' }),
      },
      totals: {
        error: {
          msg: totalsError,
        },
        notes: {
          cash: {
            receivedFromUser: cashReceived,
            deliveredToUser: cashDelivered,
          },
        },
      },
      handshake: {
        title: useTLang({ id: 'print.invoice.handshake.title' }),
        description: useTLang({ id: 'print.invoice.handshake.description' }),
        manualCode: handshakeManualCode,
      },
      user: {
        ...(userType && {
          type: useTLang({ id: `print.invoice.user.${userType}` }),
        }),
      },
    },

    checklist: {
      header: {
        orderId: orderIdTitle,
        storeName,
      },
      title: {
        p1: useTLang({ id: 'print.checklist.title.p1' }),
        p2: checklistTitleP2,
      },
      item: {
        report: {
          defectRate: useTLang({
            id: 'print.checklist.item.report.defectRate',
          }),
        },
        comment: useTLang({ id: 'print.checklist.item.comment' }),
      },
    },
  };
}

interface Props {
  orderId: OrderDetailToPrint['id'];
  storeName: OrderDetailToPrint['store']['name'];
  contact: OrderDetailToPrint['user']['contact'];
  paymentMethod: OrderDetailToPrint['paymentMethod'];
  numberOfProducts: number;
  totalsErrorMsg: string;
  cashReceivedFromUser: OrderDetailToPrint['cashReceivedFromUser'];
  // float as currency, can be negative or NaN
  cashDeliveredToUser: number;
  manualCode: OrderDetailToPrint['handshake']['manualCode'];
  userType: UserType;
}
